import { useState, useEffect, useCallback, useRef } from 'react';
import { buildApiUrl, getIranNow, getIranDayOfWeek } from '../data/constants';

const CACHE_KEY = 'aa_v3_lite';   // نسخه جدید — لیست سبک
const CACHE_TTL = 10 * 60 * 1000; // 10 دقیقه

/* ── normalize سبک ──────────────────────────────── */
function normalize(m) {
  const days_array = Array.isArray(m.days_array)
    ? m.days_array.map(Number)
    : (typeof m.days_of_week === 'string'
        ? (() => { try { return JSON.parse(m.days_of_week).map(Number); } catch { return []; } })()
        : []);

  return {
    ...m,
    days_array,
    facilities:    Array.isArray(m.facilities) ? m.facilities : [],
    active_alerts: Array.isArray(m.active_alerts) ? m.active_alerts.filter(a => a && a.message) : [],
    schedules:     Array.isArray(m.schedules) ? m.schedules : [],
    id:          Number(m.id),
    city_id:     m.city_id     ? Number(m.city_id)     : null,
    province_id: m.province_id ? Number(m.province_id) : null,
    zone_id:     m.zone_id     ? Number(m.zone_id)     : null,
    latitude:    m.latitude    ? parseFloat(m.latitude)  : null,
    longitude:   m.longitude   ? parseFloat(m.longitude) : null,
  };
}

/* ── useAllMeetings — لیست سبک، یک درخواست ───────── */
function useAllMeetings() {
  const [meetings, setMeetings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [offline,  setOffline]  = useState(false);
  const abort = useRef(null);

  const fetch_ = useCallback(async (force = false) => {
    if (!force) {
      try {
        const c = localStorage.getItem(CACHE_KEY);
        if (c) {
          const { d, t } = JSON.parse(c);
          if (Date.now() - t < CACHE_TTL && Array.isArray(d) && d.length) {
            setMeetings(d.map(normalize));
            setLoading(false);
            // در پس‌زمینه تازه‌سازی کن تا جلسات جدید زود بیایند
            refreshInBackground();
            return;
          }
        }
      } catch {}
    }

    abort.current?.abort();
    abort.current = new AbortController();
    setLoading(true);
    setError(null);

    try {
      const url = buildApiUrl({ action: 'list_lite' });
      const res = await fetch(url, { signal: abort.current.signal, cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const data = json.success ? (json.data || []) : [];

      setMeetings(data.map(normalize));
      setOffline(false);
      setError(null);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ d: data, t: Date.now() })); } catch {}

    } catch (e) {
      if (e.name === 'AbortError') return;
      try {
        const c = localStorage.getItem(CACHE_KEY);
        if (c) { setMeetings(JSON.parse(c).d.map(normalize)); setOffline(true); }
        else setError('خطا در اتصال به سرور');
      } catch { setError('خطا در اتصال به سرور'); }
    } finally {
      setLoading(false);
    }
  }, []);

  // تازه‌سازی بی‌صدا در پس‌زمینه (بدون loading) — جلسات جدید را می‌آورد
  const refreshInBackground = useCallback(async () => {
    try {
      const url = buildApiUrl({ action: 'list_lite' });
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) return;
      const json = await res.json();
      const data = json.success ? (json.data || []) : [];
      if (data.length) {
        setMeetings(data.map(normalize));
        try { localStorage.setItem(CACHE_KEY, JSON.stringify({ d: data, t: Date.now() })); } catch {}
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetch_();
    return () => abort.current?.abort();
  }, [fetch_]);

  useEffect(() => {
    const on  = () => { setOffline(false); fetch_(true); };
    const off = () => setOffline(true);
    // وقتی کاربر به اپ برمی‌گردد، تازه‌سازی کن
    const onVisible = () => { if (document.visibilityState === 'visible') refreshInBackground(); };
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online',  on);
      window.removeEventListener('offline', off);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetch_, refreshInBackground]);

  return { meetings, loading, error, isOffline: offline, refresh: () => fetch_(true) };
}

/* ── useMeetings — فیلتر client-side ─────────────── */
export function useMeetings(filters = {}) {
  const { meetings: all, loading, error, isOffline, refresh } = useAllMeetings();

  const meetings = all.filter(m => {
    if (filters.type        && m.type        !== filters.type)        return false;
    if (filters.gender_type && m.gender_type !== filters.gender_type) return false;
    if (filters.province_id && Number(m.province_id) !== Number(filters.province_id)) return false;
    if (filters.city_id     && Number(m.city_id)     !== Number(filters.city_id))     return false;
    if (filters.zone_id     && Number(m.zone_id)     !== Number(filters.zone_id))     return false;
    if (filters.day && !(m.days_array || []).includes(Number(filters.day))) return false;
    return true;
  });

  return { meetings, loading, error, isOffline, refresh };
}

/* ── useMeetingDetail — جزئیات کامل یک جلسه ──────── */
export function useMeetingDetail(id) {
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!id) { setMeeting(null); return; }
    setLoading(true);
    fetch(buildApiUrl({ action: 'get', id }), { cache: 'no-store' })
      .then(r => r.json())
      .then(j => setMeeting(j.success ? normalize(j.data) : null))
      .catch(() => setMeeting(null))
      .finally(() => setLoading(false));
  }, [id]);
  return { meeting, loading };
}

/* ── useZones ───────────────────────────────────── */
export function useZones(cityId) {
  const [zones,   setZones]   = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!cityId) { setZones([]); return; }
    setLoading(true);
    fetch(buildApiUrl({ action: 'zones', city_id: cityId }))
      .then(r => r.json()).then(j => setZones(j.data || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [cityId]);
  return { zones, loading };
}

/* ── useProvinces ───────────────────────────────── */
export function useProvinces() {
  const [provinces, setProvinces] = useState([]);
  useEffect(() => {
    const ck = 'aa_v2_provinces';
    try {
      const c = localStorage.getItem(ck);
      if (c) { const { d, t } = JSON.parse(c); if (Date.now()-t < 3600000) { setProvinces(d); return; } }
    } catch {}
    fetch(buildApiUrl({ action: 'provinces' }))
      .then(r => r.json()).then(j => {
        const d = j.data || [];
        setProvinces(d);
        try { localStorage.setItem(ck, JSON.stringify({ d, t: Date.now() })); } catch {}
      }).catch(() => {});
  }, []);
  return provinces;
}

/* ── useCities ──────────────────────────────────── */
export function useCities(provinceId) {
  const [cities, setCities] = useState([]);
  useEffect(() => {
    if (!provinceId) { setCities([]); return; }
    fetch(buildApiUrl({ action: 'cities', province_id: provinceId }))
      .then(r => r.json()).then(j => setCities(j.data || []))
      .catch(() => {});
  }, [provinceId]);
  return cities;
}

/* ── useStats ───────────────────────────────────── */
export function useStats() {
  const [data, setData]       = useState(null);
  const [isLoading, setLoad]  = useState(true);
  const [error, setError]     = useState(null);
  useEffect(() => {
    setLoad(true);
    fetch(buildApiUrl({ action: 'stats' }))
      .then(r => r.json())
      .then(j => { setData(j.data || null); setError(j.data ? null : 'no-data'); })
      .catch(() => setError('fetch-failed'))
      .finally(() => setLoad(false));
  }, []);
  return { data, isLoading, error };
}

/* ── محاسبه وضعیت client-side ────────────────────── */
function calcStatus(startTime, duration, now) {
  if (!startTime) return { session_status: 'ended', progress: 0, minutesLeft: 0 };
  const [h, min] = startTime.substring(0, 5).split(':').map(Number);
  const start = new Date(now); start.setHours(h, min, 0, 0);
  const end   = new Date(start.getTime() + (duration || 90) * 60000);
  const ts    = now.getTime();
  if (ts >= start.getTime() && ts < end.getTime()) {
    return {
      session_status: 'live',
      progress: Math.min(100, Math.round(((ts - start.getTime()) / ((duration||90)*60000)) * 100)),
      minutesLeft: Math.ceil((end.getTime() - ts) / 60000),
    };
  }
  if (ts < start.getTime()) {
    return { session_status: 'upcoming', progress: 0, minutesLeft: Math.ceil((start.getTime() - ts) / 60000) };
  }
  return { session_status: 'ended', progress: 100, minutesLeft: 0 };
}

/* ── useTodayStatus ─────────────────────────────── */
export function useTodayStatus(meetings) {
  const now = getIranNow();
  const dow = getIranDayOfWeek(now);

  return meetings
    .filter(m => (m.days_array || []).includes(dow))
    .map(m => {
      const apiSch = (m.schedules || []).find(s => Number(s.day_of_week) === dow);
      if (apiSch?.session_status) {
        return {
          ...m,
          session_status:  apiSch.session_status,
          progress:        apiSch.progress || 0,
          minutesLeft:     apiSch.minutes_until_end || apiSch.minutes_until_start || 0,
          todayStartTime:  apiSch.start_time?.substring(0, 5) || '',
        };
      }
      const st = m.first_start_time || '';
      const s  = calcStatus(st, m.duration, now);
      return { ...m, ...s, todayStartTime: st.substring(0, 5) };
    })
    .sort((a, b) => {
      const order = { live: 0, upcoming: 1, ended: 2 };
      if (order[a.session_status] !== order[b.session_status])
        return order[a.session_status] - order[b.session_status];
      return (a.todayStartTime || '').localeCompare(b.todayStartTime || '');
    });
}

/* ── haversine ──────────────────────────────────── */
function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371, dLat = (lat2-lat1)*Math.PI/180, dLng = (lng2-lng1)*Math.PI/180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)) * 10) / 10;
}

/* ── useNearest ─────────────────────────────────── */
export function useNearest() {
  const [nearest,  setNearest]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [location, setLocation] = useState(null);

  const find = useCallback(() => {
    if (!navigator.geolocation) { setError('GPS پشتیبانی نمی‌شود'); return; }
    setLoading(true); setError(null);

    navigator.geolocation.getCurrentPosition(async pos => {
      const { latitude: lat, longitude: lng } = pos.coords;
      setLocation({ lat, lng });
      try {
        const res  = await fetch(buildApiUrl({ action: 'nearest', lat, lng, limit: 10 }));
        const json = await res.json();
        if (json.success && json.data?.length) {
          setNearest(json.data.map(m => ({
            ...normalize(m),
            distance_km: haversine(lat, lng, parseFloat(m.latitude), parseFloat(m.longitude))
          })));
        } else throw new Error('no data');
      } catch {
        try {
          const c = localStorage.getItem(CACHE_KEY);
          if (c) {
            const data = JSON.parse(c).d
              .filter(m => m.latitude && m.longitude)
              .map(m => ({ ...normalize(m), distance_km: haversine(lat, lng, parseFloat(m.latitude), parseFloat(m.longitude)) }))
              .sort((a, b) => a.distance_km - b.distance_km).slice(0, 10);
            setNearest(data);
          } else setError('جلسه‌ای با موقعیت جغرافیایی یافت نشد');
        } catch { setError('خطا'); }
      } finally { setLoading(false); }
    }, err => {
      setLoading(false);
      setError({ 1:'دسترسی رد شد', 2:'موقعیت در دسترس نیست', 3:'زمان به پایان رسید' }[err.code] || 'خطای GPS');
    }, { timeout: 10000, maximumAge: 300000 });
  }, []);

  return { nearest, loading, error, location, find };
}
